/* global React, ReactDOM */
/* global StatusBar, Icon */
/* global MapScreen, DirectoryScreen, TideScreen, AboutScreen, SidePanel, BottomSheet */
/* global useTweaks, TweaksPanel, TweakSection, TweakRadio, TweakColor, TweakToggle, TweakSelect */
/* global FIXED_REGIONS, adaptRecord, loadRegionRecords */

const { useState, useEffect, useRef, useMemo } = React;

function getInitialAppLang() {
  try {
    const saved = localStorage.getItem('crmaps-lang');
    if (saved === 'es' || saved === 'en') return saved;
  } catch {}
  return (navigator.language || '').toLowerCase().startsWith('es') ? 'es' : 'en';
}

// Pestaña inicial desde la URL (?tab=directory | tide); la usa el hub /go para
// abrir la app directo en el directorio o en las mareas. Sin parámetro: mapa.
function getInitialTab() {
  try {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'directory' || tab === 'tide') return tab;
  } catch {}
  return 'map';
}

// Mapa inicial desde la URL (?map=PJ | TA | FA | MA | CH | DU | HE): las páginas de
// destino en móvil no incrustan el visor, mandan a la app directo a su mapa.
// ?client=<id> centra además en ese hotspot («Cerca de acá» de la ficha).
// Sin parámetro, o con un id desconocido: Costa Rica.
function getInitialRegion() {
  try {
    const id = (new URLSearchParams(location.search).get('map') || '').toUpperCase();
    if (id && FIXED_REGIONS.some(r => r.id === id)) return { id };
  } catch {}
  return null;
}
function getInitialClient() {
  try { return new URLSearchParams(location.search).get('client') || null; } catch { return null; }
}
const INITIAL_CLIENT = getInitialClient();

const APP_COPY = {
  en: {
    tabs: {
      map: 'Map',
      directory: 'Directory',
      tide: 'Weather & Tides',
      web: 'Web',
    },
    map: {
      selectMap: 'Select map',
      filterCategory: 'Filter by category',
      loadingHotspots: 'Loading hotspots...',
    },
    directory: {
      kicker: 'Costa Rica · Directory',
      title: 'Find your next spot.',
      search: 'Search',
      allMaps: 'All Maps',
      mapSelector: 'Map selector',
      categorySelector: 'Category selector',
      filterCategory: 'Filter by category',
      loading: 'Loading...',
      empty: 'No places found.',
      pass: 'Pass',
    },
  },
  es: {
    tabs: {
      map: 'Mapa',
      directory: 'Directorio',
      tide: 'Clima & Mareas',
      web: 'Web',
    },
    map: {
      selectMap: 'Selecciona mapa',
      filterCategory: 'Filtrar por categoria',
      loadingHotspots: 'Cargando hotspots...',
    },
    directory: {
      kicker: 'Costa Rica · Directorio',
      title: 'Encuentra tu proximo lugar.',
      search: 'Buscar',
      allMaps: 'Todos los mapas',
      mapSelector: 'Selector de mapa',
      categorySelector: 'Selector de categoria',
      filterCategory: 'Filtrar por categoria',
      loading: 'Cargando...',
      empty: 'No se encontraron lugares.',
      pass: 'Pase',
    },
  },
};

// ── Background prefetch ────────────────────────────────────────────────────────
// Fires immediately on first render. Stores one Promise per region in
// window.CRMAPS_PREFETCH so DirectoryScreen can await them without re-fetching.
function initPrefetch() {
  if (window.CRMAPS_PREFETCH) return; // already started
  const prefetch = window.CRMAPS_PREFETCH = {};

  FIXED_REGIONS.forEach(region => {
    // Warm HTTP cache for DZI descriptor — low priority so OSD tiles aren't competing
    fetch(`/tiles/${region.dziName.replace(/ /g, '%20')}`, { priority: 'low' }).catch(() => {});

    // Fetch + adapt the same merged hotspot data used by the map view.
    prefetch[region.id] = loadRegionRecords(region.id)
      .then(records => {
        const adapted = records.map((r, i) => ({ ...adaptRecord(r, i), region: region.id }));
        // Preload hero images at low priority
        adapted.forEach(h => {
          if (h.hero) {
            const img = new Image();
            img.fetchPriority = 'low';
            img.src = h.hero;
          }
        });
        return adapted;
      })
      .catch(() => []);
  });
}

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "popupVariant": "side",
  "panelStyle": "premium",
  "accent": "#0061b4",
  "showCategoryRail": true,
  "showTidePill": true,
  "discountStyle": "wallet",
  "background": "blue"
}/*EDITMODE-END*/;

function TabBar({ active, onChange, copy }) {
  const tabs = [
    { id: "map", label: copy.tabs.map, icon: (
      <svg className="tab-ico" viewBox="0 0 24 24">
        <path d="M9 3 3 6v15l6-3 6 3 6-3V3l-6 3-6-3z"/><path d="M9 3v15"/><path d="M15 6v15"/>
      </svg>
    )},
    { id: "directory", label: copy.tabs.directory, icon: (
      <svg className="tab-ico" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/>
      </svg>
    )},
    { id: "tide", label: copy.tabs.tide, icon: (
      <svg className="tab-ico" viewBox="0 0 24 24">
        <path d="M2 12c2.5-3 5-3 7.5 0s5 3 7.5 0 5-3 7.5 0"/>
        <path d="M2 18c2.5-3 5-3 7.5 0s5 3 7.5 0 5-3 7.5 0" opacity="0.5"/>
      </svg>
    )},
    { id: "web", label: copy.tabs.web, href: "../", icon: (
      <svg className="tab-ico" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9"/>
        <path d="M3 12h18"/>
        <path d="M12 3c2.3 2.5 3.5 5.5 3.5 9s-1.2 6.5-3.5 9"/>
        <path d="M12 3c-2.3 2.5-3.5 5.5-3.5 9s1.2 6.5 3.5 9"/>
      </svg>
    )},
  ];
  return (
    <nav className="tabbar">
      {tabs.map((t) => (
        t.href ? (
          <a
            key={t.id}
            className="tab-btn"
            href={t.href}
          >
            {t.icon}
            <span>{t.label}</span>
            <span className="tab-pill" />
          </a>
        ) : (
          <button
            key={t.id}
            className={"tab-btn" + (active === t.id ? " is-active" : "")}
            onClick={() => onChange(t.id)}
          >
            {t.icon}
            <span>{t.label}</span>
            <span className="tab-pill" />
          </button>
        )
      ))}
    </nav>
  );
}

function trackEvent(data) {
  try {
    let uid = localStorage.getItem('crmaps_uid');
    if (!uid) {
      uid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('crmaps_uid', uid);
    }
    let sid = sessionStorage.getItem('crmaps_sid');
    if (!sid) {
      sid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('crmaps_sid', sid);
    }
    const attribution = typeof window.CRMapsAnalyticsContext === 'function' ? window.CRMapsAnalyticsContext() : {};
    const payload = JSON.stringify({ ...data, ...attribution, userId: uid, sessionId: sid, ts: new Date().toISOString() });
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/analytics/track', new Blob([payload], { type: 'application/json' }));
    } else {
      fetch('/api/analytics/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
    }
  } catch {}
}
window.trackEvent = trackEvent;

function App() {
  const [t, setT] = useTweaks(TWEAK_DEFAULTS);
  const [tab, setTab] = useState(getInitialTab);
  const [activeHs, setActiveHs] = useState(null);
  const [hsFromDir, setHsFromDir] = useState(false); // panel opened from the Directory → DZI map
  const [targetRegion, setTargetRegion] = useState(getInitialRegion);
  const [lang, setLang] = useState(getInitialAppLang);
  const copy = APP_COPY[lang] || APP_COPY.en;

  useEffect(() => { initPrefetch(); }, []);
  useEffect(() => { trackEvent({ type: 'map_view', mapCode: targetRegion?.id || 'CR' }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'tide') trackEvent({ type: 'tide_view' }); }, []); // llegada directa con ?tab=tide

  useEffect(() => {
    try { localStorage.setItem('crmaps-lang', lang); } catch {}
    document.documentElement.lang = lang;
  }, [lang]);

  const pushedRef  = useRef(false); // true when we own the current history entry
  const activeHsRef = useRef(null); // ref copy to read inside popstate handler
  const currentMapRef = useRef(targetRegion?.id || 'CR'); // map on screen, for the way back from a ficha

  const openHotspot = (hs, fromDir = false) => {
    trackEvent({ type: 'popup_open', company: hs.name, clientId: hs.id || '', mapCode: hs.region || '' });
    history.pushState({ crmaps_popup: true }, '');
    pushedRef.current   = true;
    activeHsRef.current = hs;
    setHsFromDir(!!fromDir);
    setActiveHs(hs);
  };

  const switchMap = (regionId) => {
    closeHotspot();
    currentMapRef.current = regionId;
    trackEvent({ type: 'map_view', mapCode: regionId });
    setTargetRegion({ id: regionId, seq: Date.now() });
    setTab("map");
  };

  const closeHotspot = (fromPopstate = false) => {
    activeHsRef.current = null;
    setActiveHs(null);
    if (!fromPopstate && pushedRef.current) {
      pushedRef.current = false;
      history.back(); // clean up the entry we pushed
    } else {
      pushedRef.current = false;
    }
  };

  useEffect(() => {
    const onPopState = () => {
      if (activeHsRef.current) closeHotspot(true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // «Descubre este lugar»: if Back from the ficha reloads the app instead of
  // restoring it, the address brings the visitor back to this tab and this map.
  const placeReturnParams = () => ({
    tab: tab === 'map' ? null : tab,
    map: currentMapRef.current && currentMapRef.current !== 'CR' ? currentMapRef.current : null,
  });

  // Apply accent color via inline style override
  const accentStyle = useMemo(() => ({
    "--crmaps-blue": t.accent || "#0061b4",
  }), [t.accent]);

  // Background variant
  const stageBg = {
    blue: "var(--crmaps-blue)",
    deep: "#0a2747",
    photo: "url('assets/brand/background.webp') center/cover, var(--crmaps-blue)",
  }[t.background || "blue"];

  return (
    <div className="stage" style={{ background: stageBg, ...accentStyle }}>
      <div className="app-root">
        {/* Screen content */}
        <div className="screen">
          {tab === "map" && (
            <MapScreen
              onHotspotClick={openHotspot}
              activeHotspot={activeHs?.id}
              onTideClick={() => setTab("tide")}
              targetRegion={targetRegion}
              focusHotspot={INITIAL_CLIENT}
              lang={lang}
              onLanguageChange={setLang}
              copy={copy.map}
              onMapChange={(id) => { currentMapRef.current = id; trackEvent({ type: 'map_view', mapCode: id }); }}
            />
          )}
          {tab === "directory" && <DirectoryScreen onCardClick={(hs) => openHotspot(hs, true)} lang={lang} onLanguageChange={setLang} copy={copy.directory} />}
          {tab === "tide" && <TideScreen onCardClick={openHotspot} lang={lang} onLanguageChange={setLang} />}
        </div>

        {/* Tab bar */}
        <TabBar active={tab} copy={copy} onChange={(id) => {
          if (id === 'tide') trackEvent({ type: 'tide_view' });
          if (id === 'map' && tab !== 'map') currentMapRef.current = targetRegion?.id || 'CR'; // MapScreen remounts on it
          setTab(id);
          setActiveHs(null);
        }} />

        {/* Hotspot popup */}
        {t.popupVariant === "side" && (
          <SidePanel hs={activeHs} onClose={closeHotspot} onSwitchMap={switchMap} placeReturnParams={placeReturnParams} lang={lang} useDziMap={hsFromDir} />
        )}
        {t.popupVariant === "sheet" && (
          <BottomSheet hs={activeHs} onClose={closeHotspot} onSwitchMap={switchMap} placeReturnParams={placeReturnParams} lang={lang} />
        )}
      </div>

      <TweaksPanel title="Tweaks" defaultOpen={false}>
        <TweakSection label="Hotspot popup">
          <TweakRadio
            label="Variant"
            value={t.popupVariant}
            onChange={(v) => setT("popupVariant", v)}
            options={[
              { value: "side", label: "Side panel" },
              { value: "sheet", label: "Bottom sheet" },
            ]}
          />
        </TweakSection>

        <TweakSection label="Theme">
          <TweakColor
            label="Accent"
            value={t.accent}
            onChange={(v) => setT("accent", v)}
            options={["#0061b4", "#10656d", "#f97316", "#16a34a"]}
          />
          <TweakRadio
            label="Background"
            value={t.background}
            onChange={(v) => setT("background", v)}
            options={[
              { value: "blue", label: "Blue" },
              { value: "deep", label: "Deep" },
              { value: "photo", label: "Photo" },
            ]}
          />
        </TweakSection>

        <TweakSection label="Map chrome">
          <TweakToggle
            label="Show tide pill"
            value={t.showTidePill}
            onChange={(v) => setT("showTidePill", v)}
          />
        </TweakSection>
      </TweaksPanel>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
